import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import {
  ShieldCheck, Warning, Clock, ArrowRight, Bandaids, XCircle,
} from "@phosphor-icons/react";

// CIP-007 R2 security state → human label + tone
const SEC_STATE = {
  open:                             { label: "Open",                       tone: "bg-secondary text-gray-700 border-gray-300" },
  evaluating:                       { label: "Evaluating Patch Sources",   tone: "bg-indigo-50 text-indigo-700 border-indigo-300" },
  no_patch_available:               { label: "No Patch Available",         tone: "bg-teal-50 text-teal-700 border-teal-300" },
  patch_available:                  { label: "Patch Available",            tone: "bg-blue-50 text-blue-700 border-blue-300" },
  patching:                         { label: "Patching",                   tone: "bg-blue-100 text-blue-800 border-blue-400" },
  mitigating:                       { label: "Mitigating",                 tone: "bg-amber-50 text-amber-800 border-amber-300" },
  mitigation_extension_requested:   { label: "Extension Requested (CSM)",  tone: "bg-orange-50 text-orange-800 border-orange-300" },
  mitigation_extension_approved:    { label: "Extension Approved (CSM)",   tone: "bg-orange-100 text-orange-900 border-orange-400" },
  patched:                          { label: "Patched",                    tone: "bg-green-50 text-green-700 border-green-300" },
  retired:                          { label: "Asset Retired / Replaced",   tone: "bg-slate-100 text-slate-700 border-slate-400" },
  spot_check:                       { label: "Spot Check",                 tone: "bg-purple-50 text-purple-700 border-purple-300" },
  spot_check_reviewed:              { label: "Spot Check Reviewed",        tone: "bg-purple-100 text-purple-800 border-purple-400" },
  closed:                           { label: "Closed",                     tone: "bg-primary text-primary-foreground border-black" },
};

const PATCH_SOURCES = [
  ["foxguard",       "Foxguard (documented)"],
  ["vendor",         "Vendor advisory (documented)"],
  ["sme_documented", "SME-created (with citations)"],
];

// Presets: which fields the SME must fill before the "next status" button unlocks.
// Purely a UX hint — the backend enforces authoritatively.
function requiredFor(target) {
  switch (target) {
    case "no_patch_available":            return ["evidence"];
    case "patch_available":               return ["patch_source"];
    case "patching":                      return [];
    case "mitigating":                    return ["mitigation_plan", "mitigation_end_date"];
    case "mitigation_extension_requested":return ["csm_extension_reason", "csm_extension_new_end_date"];
    case "mitigation_extension_approved": return ["role_admin"];
    case "patched":                       return ["patched_version", "evidence"];
    case "retired":                       return ["retirement_reason", "evidence"];
    case "spot_check_reviewed":           return ["role_reviewer_or_admin"];
    default: return [];
  }
}

export default function SecurityTicketPanel({ ticket, onChange }) {
  const { user, canEdit, isRole } = useAuth();
  const [assets, setAssets] = useState([]);
  const [form, setForm] = useState({
    bes_asset_id: ticket.bes_asset_id || "",
    evaluation_date: ticket.evaluation_date || "",
    patch_source: ticket.patch_source || "",
    patch_source_note: ticket.patch_source_note || "",
    mitigation_plan: ticket.mitigation_plan || "",
    mitigation_start_date: ticket.mitigation_start_date || "",
    mitigation_end_date: ticket.mitigation_end_date || "",
    patched_version: ticket.patched_version || "",
    retirement_reason: ticket.retirement_reason || "",
    csm_extension_reason: ticket.csm_extension_reason || "",
    csm_extension_new_end_date: ticket.csm_extension_new_end_date || "",
    compliance_notes: ticket.compliance_notes || "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/cip002/bes-assets").then((r) => setAssets(r.data?.bes_assets || [])).catch(() => setAssets([]));
  }, []);

  useEffect(() => {
    // when ticket refreshes from parent, sync fields
    setForm({
      bes_asset_id: ticket.bes_asset_id || "",
      evaluation_date: ticket.evaluation_date || "",
      patch_source: ticket.patch_source || "",
      patch_source_note: ticket.patch_source_note || "",
      mitigation_plan: ticket.mitigation_plan || "",
      mitigation_start_date: ticket.mitigation_start_date || "",
      mitigation_end_date: ticket.mitigation_end_date || "",
      patched_version: ticket.patched_version || "",
      retirement_reason: ticket.retirement_reason || "",
      csm_extension_reason: ticket.csm_extension_reason || "",
      csm_extension_new_end_date: ticket.csm_extension_new_end_date || "",
      compliance_notes: ticket.compliance_notes || "",
    });
  }, [ticket.id, ticket.updated_at]);

  const sla = ticket.sla || {};
  const allowedNext = sla.allowed_next_statuses || [];

  const saveFields = async () => {
    setSaving(true);
    try {
      // Merge our fields with the base ticket for the PUT payload
      const payload = {
        ...ticket,
        ...form,
        due_date: ticket.due_date || null,
        tracker_item_id: ticket.tracker_item_id || null,
      };
      await api.put(`/flow/tickets/${ticket.id}`, payload);
      toast.success("CIP-007 R2 details saved");
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const moveTo = async (status) => {
    try {
      await api.post(`/flow/tickets/${ticket.id}/status`, { status });
      toast.success(`Moved to ${SEC_STATE[status]?.label || status}`);
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const stateInfo = SEC_STATE[ticket.status] || { label: ticket.status, tone: "bg-gray-100 border-gray-300" };
  const canApproveCsm = isRole("admin");

  const _fieldsMissing = (target) => {
    const req = requiredFor(target);
    const missing = [];
    for (const k of req) {
      if (k === "evidence" && !(ticket.evidence || []).length) missing.push("evidence");
      else if (k === "role_admin" && user?.role !== "admin") missing.push("admin role required");
      else if (k === "role_reviewer_or_admin" && !["admin", "reviewer"].includes(user?.role)) missing.push("compliance role required");
      else if (["evidence", "role_admin", "role_reviewer_or_admin"].indexOf(k) < 0 && !form[k]) missing.push(k);
    }
    return missing;
  };

  return (
    <div className="space-y-4" data-testid="sec-ticket-panel">
      {/* CIP-007 R2 SLA badges */}
      <div className="border border-border bg-secondary/30 p-3 space-y-2" data-testid="sec-sla-badges">
        <div className="overline text-muted-foreground flex items-center gap-1.5">
          <ShieldCheck size={14} weight="bold" /> CIP-007 R2 · Patch Management SLA
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className={`inline-flex items-center gap-1 border px-2 py-0.5 font-semibold uppercase tracking-wide ${stateInfo.tone}`}>
            {stateInfo.label}
          </span>
          {sla.action_due && (
            <span
              className={`inline-flex items-center gap-1 border px-2 py-0.5 ${
                sla.action_overdue ? "border-red-400 bg-red-50 text-red-700" :
                (sla.action_days_left != null && sla.action_days_left <= 5)
                  ? "border-amber-400 bg-amber-50 text-amber-700"
                  : "border-slate-300 bg-white text-slate-700"
              }`}
              data-testid="sec-sla-action"
            >
              <Clock size={12} /> Patch/Mitigate by {sla.action_due}
              {sla.action_days_left != null && (
                <b className="ml-1">
                  {sla.action_overdue ? `+${Math.abs(sla.action_days_left)}d overdue` : `${sla.action_days_left}d left`}
                </b>
              )}
            </span>
          )}
          {sla.next_eval_due && (
            <span className="inline-flex items-center gap-1 border border-slate-300 bg-white text-slate-700 px-2 py-0.5" data-testid="sec-sla-next-eval">
              <Clock size={12} /> Next eval {sla.next_eval_due}
            </span>
          )}
          {sla.mitigation_overdue && (
            <span className="inline-flex items-center gap-1 border border-red-400 bg-red-50 text-red-700 px-2 py-0.5" data-testid="sec-sla-mitigation">
              <Warning size={12} /> Mitigation end past — CSM approval required
            </span>
          )}
        </div>
      </div>

      {/* Editable CIP-007 R2 fields */}
      <div className="border border-border p-3 space-y-3" data-testid="sec-fields">
        <div className="overline text-muted-foreground">Evaluation & BES asset</div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">BES Asset</Label>
            <Select value={form.bes_asset_id || "none"} onValueChange={(v) => setForm({ ...form, bes_asset_id: v === "none" ? "" : v })} disabled={!canEdit}>
              <SelectTrigger data-testid="sec-asset-select" className="rounded-none mt-1"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">—</SelectItem>
                {assets.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Evaluation date (R2.2)</Label>
            <Input type="date" value={form.evaluation_date} onChange={(e) => setForm({ ...form, evaluation_date: e.target.value })}
                   disabled={!canEdit} data-testid="sec-eval-date" className="rounded-none mt-1" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Patch source (documented)</Label>
            <Select value={form.patch_source || "none"} onValueChange={(v) => setForm({ ...form, patch_source: v === "none" ? "" : v })} disabled={!canEdit}>
              <SelectTrigger data-testid="sec-patch-source" className="rounded-none mt-1"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">—</SelectItem>
                {PATCH_SOURCES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Patch source citation / notes</Label>
            <Input value={form.patch_source_note} onChange={(e) => setForm({ ...form, patch_source_note: e.target.value })}
                   disabled={!canEdit} placeholder="e.g. Foxguard PSR-2026-02-11, KB5034441" data-testid="sec-patch-source-note" className="rounded-none mt-1" />
          </div>
        </div>

        <div className="overline text-muted-foreground pt-1">Remediation path</div>
        <div>
          <Label className="text-xs">Mitigation plan (addressing threat as deployed)</Label>
          <Textarea value={form.mitigation_plan} onChange={(e) => setForm({ ...form, mitigation_plan: e.target.value })}
                    disabled={!canEdit} data-testid="sec-mitigation-plan" className="rounded-none mt-1"
                    placeholder="Describe how the mitigation reduces the specific threat as this asset is deployed in your system." />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Mitigation start</Label>
            <Input type="date" value={form.mitigation_start_date} onChange={(e) => setForm({ ...form, mitigation_start_date: e.target.value })}
                   disabled={!canEdit} data-testid="sec-mitigation-start" className="rounded-none mt-1" />
          </div>
          <div>
            <Label className="text-xs">Mitigation end (time-range)</Label>
            <Input type="date" value={form.mitigation_end_date} onChange={(e) => setForm({ ...form, mitigation_end_date: e.target.value })}
                   disabled={!canEdit} data-testid="sec-mitigation-end" className="rounded-none mt-1" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Patched software version</Label>
            <Input value={form.patched_version} onChange={(e) => setForm({ ...form, patched_version: e.target.value })}
                   disabled={!canEdit} data-testid="sec-patched-version" className="rounded-none mt-1"
                   placeholder="e.g. openssl 3.0.13-1ubuntu3.5" />
          </div>
          <div>
            <Label className="text-xs">Retirement / replacement reason</Label>
            <Input value={form.retirement_reason} onChange={(e) => setForm({ ...form, retirement_reason: e.target.value })}
                   disabled={!canEdit} data-testid="sec-retirement-reason" className="rounded-none mt-1"
                   placeholder="e.g. Win Server 2012 EOL — replaced with supported Win Server 2022" />
          </div>
        </div>

        <div className="overline text-muted-foreground pt-1 flex items-center gap-1.5">
          <Bandaids size={13} /> CSM extension (only if mitigation exceeds planned window)
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Justification for extension</Label>
            <Input value={form.csm_extension_reason} onChange={(e) => setForm({ ...form, csm_extension_reason: e.target.value })}
                   disabled={!canEdit} data-testid="sec-csm-reason" className="rounded-none mt-1"
                   placeholder="Why does mitigation need more time?" />
          </div>
          <div>
            <Label className="text-xs">Proposed new mitigation end</Label>
            <Input type="date" value={form.csm_extension_new_end_date} onChange={(e) => setForm({ ...form, csm_extension_new_end_date: e.target.value })}
                   disabled={!canEdit} data-testid="sec-csm-new-end" className="rounded-none mt-1" />
          </div>
        </div>
        {ticket.csm_extension_approver && (
          <div className="text-xs text-emerald-700 border border-emerald-300 bg-emerald-50 p-2" data-testid="sec-csm-approved-label">
            Extension approved by <b>{ticket.csm_extension_approver}</b>
            {ticket.csm_extension_approved_at && <> on {String(ticket.csm_extension_approved_at).slice(0, 10)}</>}
            {" — new mitigation end: "}<b>{ticket.mitigation_end_date || form.mitigation_end_date}</b>
          </div>
        )}

        <div>
          <Label className="text-xs">Compliance / spot-check notes</Label>
          <Textarea value={form.compliance_notes} onChange={(e) => setForm({ ...form, compliance_notes: e.target.value })}
                    disabled={!isRole("admin", "reviewer")} data-testid="sec-compliance-notes" className="rounded-none mt-1"
                    placeholder="Compliance spot-check findings (admin/reviewer only)" />
        </div>

        {canEdit && (
          <div className="pt-1">
            <Button onClick={saveFields} disabled={saving} data-testid="sec-save-fields" size="sm" className="rounded-none bg-primary hover:bg-primary/90">
              {saving ? "Saving…" : "Save CIP-007 R2 details"}
            </Button>
          </div>
        )}
      </div>

      {/* State transition buttons */}
      {isRole("admin", "analyst", "reviewer") && allowedNext.length > 0 && (
        <div className="border border-border p-3">
          <div className="overline text-muted-foreground mb-2 flex items-center gap-1.5">
            <ArrowRight size={13} /> Advance ticket (CIP-007 R2 lifecycle)
          </div>
          <div className="flex flex-wrap gap-2" data-testid="sec-status-actions">
            {allowedNext.map((k) => {
              const st = SEC_STATE[k] || { label: k, tone: "" };
              const missing = _fieldsMissing(k);
              // Special CSM-only guard
              const csmGate = k === "mitigation_extension_approved" && !canApproveCsm;
              const disabled = csmGate;
              return (
                <div key={k} className="flex flex-col gap-0.5">
                  <Button
                    size="sm"
                    variant={k === "closed" ? "outline" : "default"}
                    onClick={() => moveTo(k)}
                    disabled={disabled}
                    data-testid={`sec-move-${k}`}
                    className={`rounded-none ${k === "closed" ? "" : "bg-primary hover:bg-primary/90"}`}
                  >
                    {k === "retired" ? <XCircle size={13} className="mr-1" /> : <ArrowRight size={13} className="mr-1" />}
                    {st.label}
                  </Button>
                  {missing.length > 0 && !disabled && (
                    <span className="text-[10px] text-amber-700">needs: {missing.join(", ")}</span>
                  )}
                  {csmGate && (
                    <span className="text-[10px] text-red-600">CSM (admin) only</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* History strip */}
      {(ticket.history || []).length > 0 && (
        <div className="border border-border p-3" data-testid="sec-history">
          <div className="overline text-muted-foreground mb-1.5">Lifecycle history</div>
          <ul className="text-xs space-y-1">
            {ticket.history.slice(-8).reverse().map((h, i) => (
              <li key={`${h.at}-${i}`} className="flex items-center gap-2">
                <span className="font-mono text-muted-foreground">{String(h.at).slice(0, 16).replace("T", " ")}</span>
                <span className="border border-border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                  {SEC_STATE[h.status]?.label || h.status}
                </span>
                <span className="text-muted-foreground">— {h.by}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
